<script setup lang="ts">
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { MAX_SECTION_TITLE_LENGTH, MIN_SECTION_TITLE_LENGTH } from '@@/bits/numbers'
import { defineAsyncComponent } from '@@/bits/vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import type { WallViz } from '@@/types'
import { useResizeObserver } from '@@/vuecomposables/resize_observer'
import type { PropType, Ref } from 'vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

const MultilineInput = defineAsyncComponent(() => import('@@/vuecomponents/multiline_input.vue'))

const props = defineProps({
  /**
   * Background color for the component.
   * Default is Light UI 400 / Dark UI 400.
   */
  backgroundColor: {
    type: String,
    default: () => undefined,
  },
  /**
   * Whether the current user can edit the title or not.
   */
  canEdit: {
    type: Boolean,
    required: true,
  },
  /**
   * Whether the title can be edited inline or not.
   */
  canEditInline: {
    type: Boolean,
    required: true,
  },
  /**
   * Dark mode.
   */
  darkMode: {
    type: Boolean,
    required: true,
  },
  /**
   * Direction.
   */
  dir: {
    type: String as PropType<'ltr' | 'rtl' | 'auto'>,
    default: 'auto',
  },
  /**
   * Edit mode.
   */
  editMode: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether to show More button (in Wall format).
   */
  xMoreButton: {
    type: Boolean,
    default: true,
  },
  /**
   * Whether to show the skewed edge at the start of the section.
   */
  xStartSkewedEdge: {
    type: Boolean,
    default: false,
  },
  /**
   * Surface font variant.
   */
  fontId: {
    type: Number as PropType<1 | 2 | 3 | 4>,
    required: true,
  },
  /**
   * Wall format.
   */
  format: {
    type: String as PropType<WallViz>,
    required: true,
    validator(value: string): boolean {
      // We only have sections in these formats
      return ['grid', 'matrix', 'shelf', 'stream', 'map', 'timeline', 'timeline_v2', 'table', 'columns'].includes(value)
    },
  },
  /**
   * Section title.
   */
  title: {
    type: String,
    required: true,
  },
  /**
   * Section id
   */
  sectionId: {
    type: Number,
    required: true,
  },
  id: {
    type: String,
    default: () => undefined,
  },
  isGrid: {
    type: Boolean,
    required: true,
  },
  isMatrix: {
    type: Boolean,
    required: true,
  },
  isShelf: {
    type: Boolean,
    required: true,
  },
  isStream: {
    type: Boolean,
    required: true,
  },
  isMap: {
    type: Boolean,
    required: true,
  },
  isTimeline: {
    type: Boolean,
    required: true,
  },
  isTimelineV1: {
    type: Boolean,
    required: true,
  },
  isTable: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits<{
  (name: 'open-actions'): void
  (name: 'open-actions-at-coordinates', e: MouseEvent): void
  (name: 'start-editing'): void
  (name: 'finish-editing', value: string): void
  (name: 'cancel-editing'): void
  (name: 'resize'): void
}>()

defineExpose({
  selectTitleInput,
})

const twFontClass = computed(() => tailwindSurfaceFontClass(props.isMap ? 1 : props.fontId))

const truncatedTitle = computed(() => {
  if ((props.isGrid || props.isShelf || props.isTimelineV1) && props.title.length > MAX_SECTION_TITLE_LENGTH) {
    return props.title.slice(0, MAX_SECTION_TITLE_LENGTH) + '…'
  }
  return props.title
})

// Only open in edit mode if user can edit inline
const isBeingEdited = computed(() => (props.canEdit && props.canEditInline ? props.editMode : false))
const titleInput = ref<InstanceType<typeof MultilineInput>>()

function selectTitleInput(): void {
  titleInput.value?.select()
}

// Select input if in edit mode
const shouldSelectInput = computed(() => isBeingEdited.value && titleInput.value)

watch(
  shouldSelectInput,
  async (shouldSelect) => {
    if (shouldSelect) {
      currentTitle.value = props.title // Set title input to latest title
      await nextTick()
      selectTitleInput()
    }
  },
  { immediate: true },
)

const currentTitle = ref(props.title)
const previousTitle = ref(props.title)

watch(
  () => props.title,
  (newTitle) => {
    previousTitle.value = newTitle
    currentTitle.value = newTitle
  },
)

const isCurrentTitleValid = computed(() => {
  const trimmedTitle = currentTitle.value.trim()
  if (trimmedTitle.length < MIN_SECTION_TITLE_LENGTH || trimmedTitle.length > MAX_SECTION_TITLE_LENGTH) {
    return false
  } else {
    return true
  }
})

function tryEditingInline(): void {
  if (props.canEdit) {
    if (props.canEditInline) {
      emit('start-editing')
    } else {
      emit('open-actions')
    }
  }
}

function finishEditing(): void {
  previousTitle.value = currentTitle.value
  emit('finish-editing', currentTitle.value)
}

function cancelEditing(): void {
  currentTitle.value = previousTitle.value
  emit('cancel-editing')
}

const $el = ref<HTMLElement>() as Ref<HTMLElement>
const skewedEdgeWidth = ref(0)

function updateSkewedEdgeWidth(): void {
  // What is this math sorcery?
  //
  // The requirement for the skewed angle is 10 degrees.
  // We can get the opposite side of the triangle by using the tangent function.
  // The additional 5 is the border radius of the skewed edge, as the edge is shifted by it.
  // Visual explanation: TBA
  skewedEdgeWidth.value = ($el.value?.getBoundingClientRect()?.height ?? 0) * Math.tan((10 * Math.PI) / 180) + 5
}

function finishEditingIfFocusTargetIsOutside(e: FocusEvent): void {
  // When MultilineInput loses focus, we don't want to emit `finish-editing` too early
  // because it may be a Tab press to focus on the Done button. To prevent this, we
  // check `e.relatedTarget` to see if it's an element inside the section title or not.
  // If it's not, then we can safely emit the event.
  if (!$el.value) return
  if (!$el.value.contains(e.relatedTarget as Node | null)) {
    emit('finish-editing', currentTitle.value)
  }
}

useResizeObserver($el, () => {
  updateSkewedEdgeWidth()
  emit('resize')
})

onMounted(() => {
  updateSkewedEdgeWidth()
})
</script>

<template>
  <div
    :id="`section-title-${sectionId}`"
    ref="$el"
    :dir="dir"
    :class="['relative', 'flex', 'flex-row', isMap && 'overflow-hidden', 'select-none']"
    @contextmenu.prevent="$emit('open-actions-at-coordinates', $event)"
  >
    <!-- START SKEWED EDGE -->
    <div
      v-if="xStartSkewedEdge"
      :class="[
        // Put the edge underneath so it doesn't cover the main content
        'z-underneath',

        'flex-none',
        'w-3',
        'rounded-[5px]',
        {
          'rounded-e-none': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
          'rounded-e-none rounded-ss-none': isMap,
        },

        // Default background color
        !backgroundColor && {
          'bg-light-ui-400': darkMode === false,
          'bg-dark-ui-400': darkMode === true,
        },

        {
          'origin-top-left': dir === 'ltr',
          'origin-top-right': dir === 'rtl',
          'origin-top-left rtl:origin-top-right': dir === 'auto',
        },
        {
          'skew-x-[10deg]': dir === 'ltr',
          'skew-x-[-10deg]': dir === 'rtl',
          'skew-x-[10deg] rtl:skew-x-[-10deg]': dir === 'auto',
        },
      ]"
      :style="{
        backgroundColor: backgroundColor,
        width: `${skewedEdgeWidth}px`,
        willChange: 'transform',
      }"
    />

    <!-- MAIN CONTENT -->
    <div
      :class="[
        // Size/shape
        {
          grow: isGrid || isShelf || isTimelineV1,
        },
        'flex',
        'items-end',
        isMap && 'overflow-hidden',
        {
          'rounded-s-none tablet-portrait:rounded-s-lg': isStream || isMatrix,
          'rounded-ss tablet-landscape:rounded-ss-lg': isMap,
          'rounded-s-lg': !xStartSkewedEdge && (isGrid || isShelf),
        },
        'py-0.75',
        'ps-0.75',
        'pe-0',

        // Default background color
        !backgroundColor && {
          'bg-light-ui-400': darkMode === false,
          'bg-dark-ui-400': darkMode === true,
        },

        // Text
        twFontClass,
        {
          'text-dark-text-100': darkMode === false,
          'text-light-text-100': darkMode === true,
        },
      ]"
      :style="{ backgroundColor: backgroundColor }"
    >
      <!-- VIEW MODE -->
      <div
        v-if="!isBeingEdited"
        :class="[
          !isTimelineV1 && 'w-full',
          {
            // If MORE BUTTON is showing, give it space
            'me-4': xMoreButton && (isGrid || isShelf || isTimelineV1) && !isBeingEdited,
          },
          canEdit && {
            'hhover:bg-light-text-300': darkMode === false,
            'hhover:bg-dark-text-300': darkMode === true,
          },
          {
            'border-2': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
            'border-[1.5px]': isMap,
          },
          'rounded-md',
          'box-border',
          'border-solid',
          'border-transparent',
          'px-1.5',
          {
            'py-0.5': isGrid || isShelf || isTimelineV1 || isStream || isTimeline || isTable || isMatrix,
            'py-0': isMap,
          },
        ]"
        @click="tryEditingInline"
        @keydown.enter="tryEditingInline"
      >
        <h2
          v-if="!isBeingEdited"
          :id="id"
          dir="auto"
          :class="[
            {
              'text-heading-3': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
              'text-body-extra-small': isMap,
            },
            'font-semibold',
            !isTimelineV1 && 'break-word-anywhere',
            isTimelineV1 && 'w-max max-w-[198px] line-clamp-2',
            'whitespace-break-spaces',
            (isMap || isTimelineV1) && 'truncate',
          ]"
          data-testid="sectionTitleText"
          v-text="truncatedTitle"
        />
      </div>

      <!-- EDIT MODE -->
      <!-- Text input -->
      <template v-if="isBeingEdited">
        <div
          :class="[
            'flex',
            {
              grow: isGrid || isShelf || isTimelineV1,
            },
            'items-center',
            {
              'border-2': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
              'border-[1.5px]': isMap,
            },
            'rounded-md',
            'box-border',
            'border-solid',
            isCurrentTitleValid && {
              'border-grape-500': darkMode === false,
              'border-canary-500': darkMode === true,
            },
            !isCurrentTitleValid && 'border-danger-100',
            'px-1.5',
            {
              'py-0.5': isGrid || isShelf || isTimelineV1 || isStream || isTimeline || isTable || isMatrix,
              'py-0': isMap,
            },
            {
              'bg-light-ui-100': darkMode === false,
              'bg-dark-ui-100': darkMode === true,
            },
            isTimelineV1 && 'max-w-[252px]',
          ]"
        >
          <MultilineInput
            ref="titleInput"
            v-model="currentTitle"
            :dark-mode="darkMode"
            :class="[
              {
                grow: isGrid || isShelf || isTimelineV1,
              },
              'm-0',
              {
                // Since we truncate the title for Grid (Shelf), we want to limit the height
                // of the input as well. This number here will ensure the height doesn't
                // jump when going from view mode to edit mode.
                'max-h-[208px]': isGrid || isShelf || isTimelineV1,
              },
              {
                'text-heading-3': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
                'text-body-extra-small': isMap,
              },
              'font-semibold',
              isTimelineV1 && '!w-auto',
              twFontClass,
            ]"
            aria-required
            :aria-label="__('Section title')"
            :cols="
              // Use cols to dynamically size the textarea to fit its contents.
              // Use currentTitle.length + 1 to accommodate characters that are wider than average.
              isStream || isMatrix || isTimeline || isTable || isMap ? currentTitle.length + 1 : undefined
            "
            data-testid="surfaceSectionRenameMultilineInput"
            @blur="finishEditingIfFocusTargetIsOutside"
            @escape="cancelEditing"
            @enter="finishEditing"
            @paste.stop
          />
        </div>

        <!-- Done button -->
        <!-- Not show on Map -->
        <OzPlainButton
          v-if="!isMap"
          :class="['ms-1', 'shrink-0', 'py-1.5']"
          :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
          :size-preset="OzPlainButtonSizePreset.H32px"
          :dark-mode="darkMode"
          :text="__('Done')"
          @blur="finishEditingIfFocusTargetIsOutside"
          @click.stop="finishEditing"
        />
      </template>
    </div>

    <!-- END SKEWED EDGE -->
    <div
      :class="[
        // Put the edge underneath so it doesn't cover the main content
        'z-underneath',

        'flex-none',
        'w-3',
        'rounded-[5px]',
        {
          'rounded-s-none': isStream || isMatrix || isTimeline || isTable || isGrid || isShelf || isTimelineV1,
          'rounded-s-none rounded-ee-none': isMap,
        },

        // Default background color
        !backgroundColor && {
          'bg-light-ui-400': darkMode === false,
          'bg-dark-ui-400': darkMode === true,
        },

        {
          'origin-bottom-left': dir === 'ltr',
          'origin-bottom-right': dir === 'rtl',
          'origin-bottom-left rtl:origin-bottom-right': dir === 'auto',
        },
        {
          'skew-x-[10deg]': dir === 'ltr',
          'skew-x-[-10deg]': dir === 'rtl',
          'skew-x-[10deg] rtl:skew-x-[-10deg]': dir === 'auto',
        },
      ]"
      :style="{
        backgroundColor: backgroundColor,
        width: `${skewedEdgeWidth}px`,
        willChange: 'transform',
      }"
    />

    <!-- MORE BUTTON -->
    <!-- Only show on Wall/Shelf/Timeline and when not editing -->
    <OzBaseButton
      v-if="xMoreButton && (isGrid || isShelf || isTimelineV1 || isTimelineV1) && !isBeingEdited"
      :id="`section-action-menu-button-${sectionId}`"
      :class="[
        // Position
        'absolute',
        'top-[3.25px]',

        // Size/shape
        'flex',
        'items-center',
        'justify-center',
        'w-6 h-6.5',
        {
          'rotate-[-10deg]': dir === 'ltr',
          'rotate-[10deg]': dir === 'rtl',
          'rotate-[-10deg] rtl:rotate-[10deg]': dir === 'auto',
        },

        // No background
        'bg-transparent',

        // Focus ring
        'focus-visible:outline-none',
        'rounded-lg',
        'focus-visible:ring-2',
        {
          'ring-grape-500': darkMode === false,
          'ring-canary-500': darkMode === true,
        },

        // Icon style
        'leading-none',
        {
          'text-dark-text-200': darkMode === false,
          'text-light-text-200': darkMode === true,
        },
        {
          'hover-hover:hover:text-dark-text-100': darkMode === false,
          'hover-hover:hover:text-light-text-100': darkMode === true,
        },
        {
          'active:text-grape-500': darkMode === false,
          'active:text-canary-500': darkMode === true,
        },
      ]"
      :style="{
        // The button is actually positioned relative to the skewed edge
        // but we can't make it a child of the skewed edge `div` because
        // of `z-underneath`.
        // 5 is the border radius of the skewed edge.
        [dir === 'rtl' ? 'left' : 'right']: `${skewedEdgeWidth - 5}px`,
      }"
      :data-testid="`${title}SectionTitleMoreButton`"
      :aria-label="__('Open section actions menu')"
      :title="__('Section actions')"
      @click.stop="$emit('open-actions-at-coordinates', $event)"
      @keydown.enter.stop.prevent="$emit('open-actions')"
      @keydown.space.stop.prevent="$emit('open-actions')"
    >
      <OzIcon name="more_vertical" :size="16" />
    </OzBaseButton>
  </div>
</template>
